import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, FileText, Loader2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { useAdminAuth } from '../context/AdminAuthContext';

const DOCUMENT_FIELDS = [
  { key: 'company_logo', label: 'Company Logo', uploadType: 'logo', accept: '.jpg,.jpeg,.png,.webp,.svg' },
  { key: 'company_brochure', label: 'Company Brochure', uploadType: 'brochure', accept: '.pdf,.doc,.docx,.ppt,.pptx' },
  { key: 'financial_statement', label: 'Latest Tax Audited Financial Statement', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png' },
  { key: 'registration_certificate', label: 'Company Registration Certificate', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
  { key: 'iata_fiata_iso', label: 'IATA / FIATA / ISO Certifications', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
  { key: 'business_license', label: 'Business License', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
  { key: 'dg_certification', label: 'DG Certification', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
  { key: 'insurance_certificate', label: 'Insurance Certificate', uploadType: 'registration_document', accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
];

const fileDisplayName = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return value.original_filename || value.file_url?.split('/').pop() || '';
  }
  return String(value).split('/').pop();
};

const getExistingDocument = (registration, key) => {
  if (!registration) return null;
  if (key === 'company_logo' || key === 'company_brochure') {
    return registration.company_info?.[key] || null;
  }
  return registration.compliance_info?.[key] || null;
};

const MemberDocumentUpload = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { api, hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [context, setContext] = useState(null);
  const [documents, setDocuments] = useState({});
  const [notes, setNotes] = useState('');

  const canValidateMembers = hasPermission('members', 'validate');

  useEffect(() => {
    const loadContext = async () => {
      try {
        const response = await api.get(`/api/admin/company-registrations/members/${memberId}/document-upload-context`);
        setContext(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to load member document details');
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [api, memberId]);

  const existingDocuments = useMemo(() => {
    const registration = context?.registration;
    return DOCUMENT_FIELDS.reduce((acc, field) => {
      acc[field.key] = getExistingDocument(registration, field.key);
      return acc;
    }, {});
  }, [context]);

  const handleUpload = async (field, file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', field.uploadType);

    try {
      setUploadingKey(field.key);
      const response = await api.post('/api/uploads/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocuments((current) => ({
        ...current,
        [field.key]: {
          file_url: response.data.file_url,
          original_filename: response.data.original_filename || file.name,
        },
      }));
      toast.success(`${field.label} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${field.label}`);
    } finally {
      setUploadingKey('');
    }
  };

  const handleSave = async () => {
    const selectedDocuments = Object.fromEntries(
      Object.entries(documents).filter(([, value]) => value?.file_url)
    );
    if (Object.keys(selectedDocuments).length === 0) {
      toast.error('Please upload at least one document before saving');
      return;
    }

    try {
      setSaving(true);
      const response = await api.post(`/api/admin/company-registrations/members/${memberId}/documents`, {
        documents: selectedDocuments,
        notes: notes.trim() || null,
      });
      toast.success(response.data?.message || 'Documents saved');
      navigate('/admin/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save documents');
    } finally {
      setSaving(false);
    }
  };

  if (!canValidateMembers) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Access denied</h1>
        <p className="mt-2 text-sm text-gray-600">You need member validation access to upload documents.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="rounded-lg bg-white p-6 shadow-sm text-gray-600">Loading document upload...</div>;
  }

  if (!context?.member) {
    return <div className="rounded-lg bg-white p-6 shadow-sm text-gray-600">Member details were not found.</div>;
  }

  const member = context.member;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Member Documents</h1>
          <p className="mt-1 text-sm text-gray-600">
            Upload required or missing documents for {member.company_name || member.email}.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/members')}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Members
        </Button>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-5 md:grid-cols-4">
          <div>
            <p className="text-xs uppercase text-gray-500">Member</p>
            <p className="font-medium text-gray-900">{member.individual_name || 'N/A'}</p>
            <p className="text-sm text-gray-500">{member.member_id}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Company</p>
            <p className="font-medium text-gray-900">{member.company_name || 'N/A'}</p>
            <p className="text-sm text-gray-500">{member.email}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Tier</p>
            <p className="font-medium text-gray-900">{member.tier || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Status / Source</p>
            <p className="font-medium text-gray-900">{member.status || 'N/A'}</p>
            <p className="text-sm text-gray-500">{member.source || 'Website'}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <FileText size={20} className="mr-2 text-blue-600" />
            Documents
          </CardTitle>
          <p className="text-sm text-gray-500">
            Existing documents remain unchanged unless you upload a replacement for that field.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {DOCUMENT_FIELDS.map((field) => {
            const existing = existingDocuments[field.key];
            const uploaded = documents[field.key];
            const currentName = fileDisplayName(uploaded || existing);
            const hasNewUpload = Boolean(uploaded?.file_url);

            return (
              <div key={field.key} className="rounded-lg border bg-white p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{field.label}</p>
                    {currentName ? (
                      <p className={`mt-1 text-sm ${hasNewUpload ? 'text-green-700' : 'text-gray-500'}`}>
                        {hasNewUpload ? 'New upload: ' : 'Existing: '}
                        {currentName}
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-gray-400">No document uploaded</p>
                    )}
                  </div>
                  {currentName && (
                    <CheckCircle size={18} className={hasNewUpload ? 'text-green-600' : 'text-gray-400'} />
                  )}
                </div>
                <label className="flex cursor-pointer items-center justify-center rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 hover:border-blue-400 hover:bg-blue-50">
                  <input
                    type="file"
                    className="hidden"
                    accept={field.accept}
                    disabled={Boolean(uploadingKey) || saving}
                    onChange={(event) => handleUpload(field, event.target.files?.[0])}
                  />
                  {uploadingKey === field.key ? (
                    <Loader2 size={16} className="mr-2 animate-spin text-blue-600" />
                  ) : (
                    <UploadCloud size={16} className="mr-2 text-blue-600" />
                  )}
                  {currentName ? 'Replace Document' : 'Upload Document'}
                </label>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-5">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-900">Admin Notes</p>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional audit note about why these documents were uploaded by admin"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/members')} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || Boolean(uploadingKey)}>
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UploadCloud size={16} className="mr-2" />}
              Save Uploaded Documents
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberDocumentUpload;
